/**
 * Cross-plugin tour search.
 *
 * Two-tier index:
 *
 *   • Tier 1 (sync) — title / summary / pluginLabel. Built from the manifest
 *     on first `searchTours` call. Available immediately, no network.
 *
 *   • Tier 2 (lazy) — adds narration text (every `narrate` / `annotate` step
 *     and step-level `narrate` field) from a prebuilt JSON shipped under
 *     `/guide-me/tours.search-index.json`. The build script
 *     `scripts/build-tour-search-index.mjs` writes that file; the runtime
 *     fetches it once on first non-trivial search and rebuilds the index
 *     in place. Until the fetch settles, Tier 1 still serves results.
 *
 * The narration prebuild is gzipped to ~9 KB for the current corpus, so the
 * upgrade is cheap; we still gate the fetch behind a search intent rather
 * than running it at boot, because most sessions never open the search tab.
 */

import { TourIndex, type TourDoc } from './search';
import {
  listAllTourSummaries,
  getTourSummaryById,
  type TourSummary,
} from './tourCatalog';
import { PLUGIN_GUIDE_MAP } from './pluginGuideMap';

export interface TourSearchHit {
  readonly tour: TourSummary;
  readonly score: number;
  readonly matchedTerms: ReadonlyArray<string>;
}

type Tier = 'manifest' | 'with-narration';

interface CachedIndex {
  readonly index: TourIndex;
  readonly tier: Tier;
}

interface NarrationPayload {
  readonly version: 1;
  readonly narration: Readonly<Record<string, string>>;
}

let cached: CachedIndex | null = null;
/** In-flight or settled narration fetch — ensures we only fetch once. */
let narrationFetch: Promise<Readonly<Record<string, string>> | null> | null = null;

const NARRATION_URL = '/guide-me/tours.search-index.json';

function buildIndex(narration: Readonly<Record<string, string>> | null): TourIndex {
  const summaries = listAllTourSummaries();
  const docs: TourDoc[] = summaries.map((s) => ({
    id: s.id,
    pluginId: s.pluginId,
    title: s.title,
    summary: s.summary,
    route: s.route,
    pluginLabel: PLUGIN_GUIDE_MAP.get(s.pluginId)?.pluginLabel,
    narration: narration ? narration[s.id] : undefined,
  }));
  return TourIndex.build(docs);
}

function fetchNarration(): Promise<Readonly<Record<string, string>> | null> {
  if (narrationFetch) return narrationFetch;
  narrationFetch = (async () => {
    try {
      const res = await fetch(NARRATION_URL, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = (await res.json()) as NarrationPayload;
      if (payload.version !== 1 || !payload.narration) {
        throw new Error(`unexpected payload (version=${payload.version})`);
      }
      return payload.narration;
    } catch (err) {
      // Fail-soft: keep using the title/summary index. Don't block the UI
      // and don't keep retrying forever — the user can refresh if the file
      // shows up later. We log once so a missing prebuild is visible in dev.
      // eslint-disable-next-line no-console
      console.warn(
        `[guide-me] tour narration index not available (${(err as Error).message ?? err}); falling back to title/summary search.`,
      );
      return null;
    }
  })();
  return narrationFetch;
}

/**
 * Kick the narration fetch + rebuild. Idempotent. Safe to call on tab
 * focus, on every keystroke, or both — the underlying promise dedupes.
 */
export function ensureNarrationIndex(): void {
  if (cached?.tier === 'with-narration') return;
  fetchNarration().then((narration) => {
    if (!narration) return;
    cached = { index: buildIndex(narration), tier: 'with-narration' };
  });
}

/**
 * Run a search across every authored tour. Lazily builds the manifest-only
 * index on first call; the narration upgrade happens asynchronously the
 * first time anyone calls `ensureNarrationIndex` (the panel does so when
 * the user focuses the search input).
 *
 * Cheap enough to call on every keystroke for ~100 tours (BM25 scan + sort
 * runs in ~1 ms locally).
 */
export function searchTours(
  query: string,
  opts?: { limit?: number; prefixLastTerm?: boolean; fuzzy?: boolean },
): TourSearchHit[] {
  if (!cached) {
    cached = { index: buildIndex(null), tier: 'manifest' };
  }
  const raw = cached.index.search(query, opts);
  // Upgrade DocMeta → TourSummary so callers get `match`, `guidePath`,
  // `stepCount`, etc. Falls back to the raw doc if a manifest entry has
  // been removed since the index was built (HMR edge case).
  return raw.map((hit) => ({
    tour: getTourSummaryById(hit.tour.id) ?? {
      id: hit.tour.id,
      pluginId: hit.tour.pluginId,
      title: hit.tour.title,
      summary: hit.tour.summary,
      route: hit.tour.route,
    },
    score: hit.score,
    matchedTerms: hit.matchedTerms,
  }));
}

/** HMR / test aid. Drops the memoized index so the next `searchTours` rebuilds. */
export function clearSearchIndex(): void {
  cached = null;
  narrationFetch = null;
}
